import styles from "./testCaseRegularItem.module.scss";
import { MethodType } from "../../../shared/components/methodType/methodType";
import {
  TestCase,
  TestCaseTemplate
} from "../../../types/testCaseTemplateTypes";
import editIcon from "../../../shared/images/edit.svg";
import copyIcon from "../../../shared/images/copy.svg";
import deleteIcon from "../../../shared/images/delete.svg";
import runIcon from "../../../shared/images/run.svg";
import { ReactComponent as MenuIcon } from "../../../shared/images/menu.svg";
import {
  useDispatch,
  useSelector
} from "react-redux";
import {
  AppDispatch,
  RootState
} from "../../../store";
import {
  deleteSelectedTestCase,
  setSelectedTestCase
} from "../../../store/slices/testCaseTemplateSlice";
import {
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { Popup } from "../../../shared/components/popup/popup";
import { useTranslation } from "react-i18next";
import { MessageWindow } from "../../../shared/components/messageWindow/messageWindow";
import { useDeleteTestCaseMutation } from "../../../api/requestService";
import { setUpdateModal } from "../../../store/slices/mainSlice";
import { ErrorType } from "../../../types/sharedTypes";
import { Server } from "../../../types/environmentTypes";
import { LoadTestSettingsModal } from "../../loadTestSettingsModal/loadTestSettingsModal";
import { useRunLoadTestMutation } from "../../../api/loadTestService";
import { RunLoadTestModal } from "../../runLoadTestModal/runLoadTestModal";
import { LoadTestSettings } from "../../../types/loadTestTypes";
import { getServer } from "../../../utils/testCaseUtils";
import { findProjectSettings } from "../../../store/selectors";

type TestCaseMenuItemProps = {
  testCase: TestCase;
  refetch: () => void;
  testCaseTemplate: TestCaseTemplate;
  onEditClick: () => void;
  onCopyClick: () => void;
}

export const TestCaseRegularItem = ({testCase, testCaseTemplate, refetch, onEditClick, onCopyClick}: TestCaseMenuItemProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const methods = useSelector((state: RootState) => state.mainSlice.methods);
  const selectedEnvironment = useSelector((state: RootState) => findProjectSettings(state).selectedEnvironment);
  const selectedVariables = useSelector((state: RootState) => state.mainSlice.selectedVariables);
  const isSandbox = useSelector((state: RootState) => state.authSlice.isSandbox);

  const menuContainer = useRef<HTMLDivElement>(null);

  const selectedTestCase = useSelector((state: RootState) => state.testCaseTemplateSlice.selectedTestCase);

  const [showMenu, setShowMenu] = useState<boolean>(false);

  const [showDeleteMessage, setShowDeleteMessage] = useState<boolean>(false);

  const [showLoadTestSettings, setShowLoadTestSettings] = useState<boolean>(false);
  const [showRunLoadTest, setShowRunLoadTest] = useState<boolean>(false);

  const [deleteTestCaseMutation, { isSuccess: isSuccessDeleteTestCase }] = useDeleteTestCaseMutation();

  const [runLoadTest, { error: errorRunLoadTest, isLoading: runLoadTestIsLoading  }] = useRunLoadTestMutation();

  useEffect(() => {
    if (isSuccessDeleteTestCase) {
      refetch();
      dispatch(setUpdateModal({ show: true, isDelete: true }))
    }
  }, [isSuccessDeleteTestCase]);

  const getContainerClass = (): string => {
    let result = styles.container;
    if (selectedTestCase.id === testCase.id) {
      result += ` ${styles.selected}`;
    }
    if (showMenu) {
      result += ` ${styles.showMenu}`;
    }
    return result;
  }

  const onClickMenuIcon = (): void => {
    if (!showMenu) {
      setShowMenu(true);
    }
  }

  const closeMenu = (): void => {
    setShowMenu(false);
  }

  const editClick = (): void => {
    setShowMenu(false);
    onEditClick();
  }

  const copyClick = (): void => {
    setShowMenu(false);
    onCopyClick();
  }

  const deleteClick = (): void => {
    setShowMenu(false);
    setShowDeleteMessage(true);
  }

  const runLoadTestClick = (): void => {
    setShowMenu(false);
    setShowLoadTestSettings(true);
  }

  const onClickItem = (): void => {
    if (selectedTestCase.id !== testCase.id) {
      dispatch(setSelectedTestCase({ testCase, template: testCaseTemplate, selectedVariables: selectedVariables }));
    }
  }

  const closeMessageWindow = (isConfirm: boolean): void => {
    if (isConfirm) {
      dispatch(deleteSelectedTestCase())
      deleteTestCaseMutation(testCase.id as number);
    }
    setShowDeleteMessage(false);
  }

  const closeLoadTestSettings = (settings?: LoadTestSettings): void => {
    if (!!settings) {
      const findMethod = methods.find(method => method.id === testCaseTemplate.methodId);
      if (!!findMethod && !!server) {
        runLoadTest({
          ...settings,
          jMeterTemplateModels: [{
            url: `${server.url || ""}${selectedTestCase.endpoint}`,
            authToken: server.authToken || "",
            testCaseName: testCase.name,
            body: selectedTestCase.body || "",
            method: findMethod.name,
            headers: selectedTestCase.requestHeaders?.map(header => ({name: header.name, value: header.value || ""})) || []
          }]
        });
        setShowRunLoadTest(true);
      }
    }
    setShowLoadTestSettings(false);
  }

  const closeRunLoadTestModal = (): void => {
    setShowRunLoadTest(false);
  }

  const server: Server | undefined = useMemo(() => {
    return getServer(testCaseTemplate, selectedEnvironment);
  }, [selectedEnvironment, testCaseTemplate]);

  return (
    <>
      <div onClick={onClickItem}
           className={getContainerClass()}>
        <div className={styles.info}>
          <div className={styles.methodType}>
            <MethodType methodId={testCaseTemplate.methodId}/>
          </div>
          <span title={testCase.name}>{testCase.name}</span>
        </div>
        <div className={styles.menu} ref={menuContainer}>
          <div className={styles.menuIcon} onClick={onClickMenuIcon}>
            <MenuIcon/>
          </div>
          <Popup isShow={showMenu}
                 classContainer={styles.popup}
                 left={-70}
                 top={5}
                 parent={menuContainer.current}
                 onClose={closeMenu}
                 child={
                   <>
                     <div className={styles.item}
                          onClick={editClick}>
                       <img src={editIcon} alt=""/>
                       {t("common.edit")}
                     </div>
                     <div className={styles.item}
                          onClick={copyClick}>
                       <img src={copyIcon} alt=""/>
                       {t("common.copy")}
                     </div>
                     {server && !isSandbox && (
                       <div className={styles.item}
                            onClick={runLoadTestClick}>
                         <img src={runIcon} alt=""/>
                         {t("loadTestSettings.run")}
                       </div>
                     )}
                     <div className={styles.item}
                          onClick={deleteClick}>
                       <img src={deleteIcon} alt=""/>
                       {t("common.delete")}
                     </div>
                   </>
                 }
          />
        </div>
      </div>
      {showDeleteMessage && (
        <MessageWindow onClose={closeMessageWindow}
                       title={t("testCase.delete")}
                       description={t("testCase.deleteDescription")}
                       primaryText={t("common.ok")} secondaryText={t("common.cancel")}/>
      )}
      {showLoadTestSettings && (<LoadTestSettingsModal onClose={closeLoadTestSettings} />)}
      <RunLoadTestModal isShow={showRunLoadTest} isLoading={runLoadTestIsLoading} errorData={errorRunLoadTest as ErrorType} closeModal={closeRunLoadTestModal}/>
    </>
  );
}